import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const MOODS = [
  { score: 1, label: "Struggling", emoji: "😔" },
  { score: 2, label: "Low", emoji: "🙁" },
  { score: 3, label: "Okay", emoji: "😐" },
  { score: 4, label: "Good", emoji: "🙂" },
  { score: 5, label: "Bright", emoji: "😊" },
];

const Mood = () => {
  const [moods, setMoods] = useState([]);
  const [score, setScore] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => { const { data } = await api.get("/mood"); setMoods(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!score) { toast.info("Choose how you're feeling first."); return; }
    await api.post("/mood", { score, note });
    setScore(null); setNote("");
    await load();
    toast.success("Mood logged. Thank you for checking in.");
  };

  const chartData = [...moods].reverse().map((m) => ({
    date: new Date(m.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    score: m.score,
  }));

  return (
    <div className="space-y-8" data-testid="mood-page">
      <div>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink">Mood</h1>
        <p className="text-ink-2 mt-2">A gentle daily check-in. No judgment, just noticing.</p>
      </div>

      <div className="bg-white rounded-3xl border border-linen-3 p-6 sm:p-8">
        <p className="text-ink-2 mb-4">How are you, right now?</p>
        <div className="grid grid-cols-5 gap-2 sm:gap-4">
          {MOODS.map((m) => (
            <button key={m.score} onClick={() => setScore(m.score)} data-testid={`mood-${m.score}`}
              className={`rounded-2xl py-4 flex flex-col items-center gap-1 border transition-all duration-300 ${score === m.score ? "bg-calm border-brand -translate-y-0.5" : "bg-linen border-linen-3 hover:bg-linen-2"}`}>
              <span className="text-3xl">{m.emoji}</span>
              <span className="text-xs text-ink-2">{m.label}</span>
            </button>
          ))}
        </div>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything you'd like to add? (optional)" className="rounded-2xl bg-linen border-linen-3 h-12 mt-5" data-testid="mood-note" />
        <Button onClick={save} className="rounded-full bg-brand hover:bg-brand-hover text-linen mt-4 px-6" data-testid="mood-save">Log mood</Button>
      </div>

      {chartData.length > 1 && (
        <div className="bg-white rounded-3xl border border-linen-3 p-6">
          <h3 className="font-serif text-2xl text-ink mb-4">Your rhythm</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#8A847A" }} axisLine={false} tickLine={false} />
              <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 12, fill: "#8A847A" }} axisLine={false} tickLine={false} width={20} />
              <Tooltip contentStyle={{ borderRadius: 16, border: "1px solid #EAE3D5" }} />
              <Line type="monotone" dataKey="score" stroke="#2C4A3B" strokeWidth={2.5} dot={{ fill: "#A65D47", r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="space-y-2">
        {moods.slice(0, 10).map((m) => (
          <div key={m.id} className="flex items-center gap-4 bg-white rounded-2xl border border-linen-3 px-5 py-3" data-testid="mood-entry">
            <span className="text-2xl">{MOODS.find((x) => x.score === m.score)?.emoji}</span>
            <div className="flex-1">
              <p className="text-sm text-ink">{MOODS.find((x) => x.score === m.score)?.label}{m.note ? ` — ${m.note}` : ""}</p>
              <p className="text-xs text-ink-mute">{new Date(m.created_at).toLocaleString()}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Mood;
